import React, { useEffect, useState } from "react"
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    InputAdornment,
    MenuItem,
    TextField,
    Typography
} from "@mui/material"
import { FranchiseRole } from "../../infrastructure/models/common/FranchiseRole"
import { CreateFranchiseUsers } from "../../infrastructure/models/transport/request/CreateFranchiseUsers"
import { Terminal } from "../../infrastructure/models/terminal"
import { COLORS } from "../../presentation/constants/colors"

interface UpdateFranchiseUserFormProps {
    defaultValues: {
        email: string
        firstname: string
        lastname: string
        role: number
        terminals: string[]
        isActive: boolean
        phone: {
            primary: string
            secondary: string
        }
        address: {
            street: string
            city: string
            state: string
            postalCode: string
        }
        franchiseId: string
    }
    terminals: Terminal[]
    onSubmit: (data: CreateFranchiseUsers) => void
    onCancel: () => void
    isLoading: boolean
}

const UpdateFranchiseUserForm: React.FC<UpdateFranchiseUserFormProps> = ({
    defaultValues,
    onSubmit,
    onCancel,
    isLoading,
    terminals,
}) => {

    const [terminalList, setTerminalList] = useState<Terminal[]>(terminals ?? [])
    const [formData, setFormData] = useState(defaultValues)

    useEffect(() => {
        setFormData(defaultValues)
    }, [defaultValues])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target
        if (name === "role") {
            setFormData((p) => ({ ...p, role: Number(value) }))
        } else if (name.startsWith("address.")) {
            const key = name.split(".")[1] as keyof typeof formData.address
            setFormData((p) => ({ ...p, address: { ...p.address, [key]: value } }))
        } else {
            setFormData((p) => ({ ...p, [name]: value }))
        }
    }

    const toggleTerminal = (id: string) => {
        setFormData((prev) => ({
            ...prev,
            terminals: prev.terminals.includes(id)
                ? prev.terminals.filter((tid) => tid !== id)
                : [...prev.terminals, id],
        }))
    }

    const getVisibleRoles = (): { label: string, value: number }[] => {
        return Object.keys(FranchiseRole)
            .filter((k) => isNaN(Number(k)))
            .map((k) => ({
                label: k.replace(/_/g, " "),
                value: FranchiseRole[k as keyof typeof FranchiseRole],
            }))
    }

    return (
        <Box
            sx={{
                p: 4,
                border: "1px solid #ccc",
                borderRadius: "8px",
                backgroundColor: "#fff",
            }}
        >
            <Typography variant="h5" gutterBottom>
                Update Franchise User
            </Typography>

            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    {/* firstname */}
                    <TextField
                        fullWidth
                        label="First Name"
                        name="firstname"
                        value={formData.firstname}
                        onChange={handleChange}
                    />
                </Grid>

                <Grid size={{ xs: 6, sm: 6 }}>
                    {/* lastname */}
                    <TextField
                        fullWidth
                        label="Last Name"
                        name="lastname"
                        value={formData.lastname}
                        onChange={handleChange}
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 4 }}>
                    {/* role dropdown */}
                    <TextField
                        select
                        fullWidth
                        label="Role"
                        name="role"
                        value={formData.role}
                        onChange={handleChange}
                    >
                        {getVisibleRoles().map(role => (
                            <MenuItem key={role.value} value={role.value}>
                                {role.label}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>

                <Grid size={{ xs: 4, sm: 4 }}>
                    <TextField
                        fullWidth
                        required
                        label="User Primary Number"
                        name="phone"
                        value={formData.phone.primary.replace("+91", "")}
                        onChange={(e) => {
                            const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10)
                            setFormData((prev) => ({
                                ...prev,
                                phone: {
                                    ...prev.phone,
                                    primary: `+91${digitsOnly}`
                                }
                            }))
                        }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">+91</InputAdornment>
                        }}
                        inputProps={{
                            maxLength: 10,
                            pattern: "\\d{10}"
                        }}
                    />
                </Grid>

                <Grid size={{ xs: 4, sm: 4 }}>
                    <TextField
                        fullWidth
                        label="User Alternate Number"
                        name="phone"
                        value={formData.phone.secondary.replace("+91", "")}
                        onChange={(e) => {
                            const digitsOnly = e.target.value.replace(/\D/g, "").slice(0, 10)
                            setFormData((prev) => ({
                                ...prev,
                                phone: {
                                    ...prev.phone,
                                    secondary: `+91${digitsOnly}`
                                }
                            }))
                        }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">+91</InputAdornment>
                        }}
                        inputProps={{
                            maxLength: 10,
                            pattern: "\\d{10}"
                        }}
                    />
                </Grid>

                <Grid size={{ xs: 6, sm: 6 }}>
                    {/* Email */}
                    <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                    />
                </Grid>

                {/* Status */}
                <Grid size={{ xs: 6, sm: 6 }}>
                    <TextField
                        select
                        fullWidth
                        label="Status"
                        name="isActive"
                        value={formData.isActive.toString()}
                        onChange={handleChange}
                    >
                        <MenuItem value="true">Active</MenuItem>
                        <MenuItem value="false">Inactive</MenuItem>
                    </TextField>
                </Grid>

                {/* Terminals of the selected franchise */}
                <Grid size={{ xs: 12 }}>
                    <Box sx={{ border: "1px solid #ccc", borderRadius: 2, p: 2, position: "relative" }}>
                        <Typography variant="subtitle1" sx={{ position: "absolute", top: "-12px", left: "16px", backgroundColor: COLORS.WHITE, px: 1 }}>
                            Select Terminals
                        </Typography>
                        <Grid container spacing={2}>
                            {terminalList.map((term) => (
                                <Grid key={term.terminalId} size={{ xs: 12, sm: 4 }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.terminals.includes(term.terminalId)}
                                                onChange={() => toggleTerminal(term.terminalId)}
                                            />
                                        }
                                        label={term.name}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Grid>

                {/* address */}
                <Grid size={{ xs: 12 }}>
                    <TextField
                        fullWidth
                        label="Street"
                        name="address.street"
                        value={formData.address.street}
                        onChange={handleChange}
                    />
                </Grid>

                <Grid size={{ xs: 5 }}>
                    <TextField
                        fullWidth
                        label="City"
                        name="address.city"
                        value={formData.address.city}
                        onChange={handleChange}
                    />
                </Grid>

                <Grid size={{ xs: 5 }}>
                    <TextField
                        fullWidth
                        label="State"
                        name="address.state"
                        value={formData.address.state}
                        onChange={handleChange}
                    />
                </Grid>

                <Grid size={{ xs: 2 }}>
                    <TextField
                        fullWidth
                        label="Postal Code"
                        name="address.postalCode"
                        value={formData.address.postalCode}
                        onChange={handleChange}
                        inputProps={{ maxLength: 6 }}
                    />
                </Grid>
            </Grid>

            <Button
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                onClick={() => {
                    console.log("Submitting:", formData)
                    onSubmit({
                        email: formData.email,
                        firstname: formData.firstname,
                        lastname: formData.lastname,
                        role: formData.role,
                        terminals: formData.terminals,
                        isActive: formData.isActive,
                        address: formData.address,
                        phone: formData.phone,
                        franchiseId: formData.franchiseId,
                    })
                }}
            >
                Update
            </Button>
        </Box>
    )
}

export default UpdateFranchiseUserForm